import { LOCALE } from '@/helpers/locale'
import { getLanguageCookie, setLanguageCookie } from '@/helpers/languageCookie'

const DEFAULT_LOCALE = 'en'

/**
 * Maps a raw language string (e.g. "fr-CA", "es") to a supported LOCALE key.
 * Returns null if no match is found.
 *
 * Supported locales: "en", "fr-ca"
 */
const mapToSupportedLocale = (lang) => {
  if (!lang) return null

  const normalized = lang.toLowerCase()

  if (LOCALE[normalized]) return normalized

  // Match by ISO code (e.g. "en-US" → "en", "fr-CA" → "fr-ca")
  const matchByIso = Object.keys(LOCALE).find(
    key => LOCALE[key].iso.toLowerCase() === normalized
  )
  if (matchByIso) return matchByIso

  // Match by base language (e.g. "fr" → "fr-ca")
  const base = normalized.split('-')[0]
  if (LOCALE[base]) return base

  return null
}

/**
 * Resolves the initial locale for the application.
 *
 * Priority:
 *   1. Shared domain cookie (userLanguagePreference)
 *   2. Browser preferred language (navigator.languages[0] || navigator.language)
 *   3. Default English
 *
 * Side-effects:
 *   - Writes / refreshes the cookie when it was absent or the resolved value differs.
 *
 * @returns {string} A valid LOCALE key.
 */
export const resolveInitialLocale = () => {
  const cookieValue = getLanguageCookie()

  if (cookieValue) {
    const mapped = mapToSupportedLocale(cookieValue)
    if (mapped) {
      setLanguageCookie(mapped)
      return mapped
    }
  }

  const browserLang =
    (navigator.languages && navigator.languages[0]) || navigator.language
  const browserMapped = mapToSupportedLocale(browserLang)

  if (browserMapped) {
    setLanguageCookie(browserMapped)
    return browserMapped
  }

  setLanguageCookie(DEFAULT_LOCALE)
  return DEFAULT_LOCALE
}
